import axios from 'axios';
import { getApiBaseUrl } from './config';
import { notifyMissionMutation } from './missionEvents';

// 创建一个函数来获取API实例，这样可以在配置更改后重新创建
let apiInstance: ReturnType<typeof axios.create> | null = null;

function getApi() {
  const baseURL = getApiBaseUrl();
  
  // 如果实例不存在或者baseURL改变了，重新创建
  if (!apiInstance || apiInstance.defaults.baseURL !== baseURL) {
    apiInstance = axios.create({
      baseURL,
      timeout: 10000, // 10秒超时
    });

    // 添加请求拦截器：注入 Token + 调试日志
    apiInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        console.log('API Request:', config.method?.toUpperCase(), config.url);
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // 添加响应拦截器用于调试
    apiInstance.interceptors.response.use(
      (response) => {
        console.log('API Response:', response.status, response.config.url);
        notifyMissionMutation(response.config.method, response.config.url, response.data);
        return response;
      },
      (error) => {
        console.error('API Response Error:', error.response?.status, error.config?.url, error.message);
        return Promise.reject(error);
      }
    );
  }
  
  return apiInstance;
}

// 重置API实例（配置更改后调用）
export function resetApiInstance() {
  apiInstance = null;
}

// ==================== 认证相关 API ====================
export const authApi = {
  login: (data: { username: string; password: string; device_name?: string }) =>
    getApi().post('/api/auth/login', data),
  register: (data: { username: string; password: string; email?: string; nickname?: string; phone?: string }) =>
    getApi().post('/api/auth/register', data),
  logout: () => getApi().post('/api/auth/logout'),
  logoutAll: () => getApi().post('/api/auth/logout-all'),
  getMe: () => getApi().get('/api/auth/me'),
  updateProfile: (data: { nickname?: string; avatar?: string; phone?: string; email?: string; bio?: string }) =>
    getApi().put('/api/auth/profile', data),
  changePassword: (data: { old_password: string; new_password: string }) =>
    getApi().post('/api/auth/change-password', data),
  getTokens: () => getApi().get('/api/auth/tokens'),
  revokeToken: (tokenId: number) => getApi().delete(`/api/auth/tokens/${tokenId}`),
  getSanStats: () => getApi().get('/api/auth/san-stats'),
  recalculateSan: () => getApi().post('/api/auth/san-recalculate'),
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return getApi().post('/api/auth/avatar', formData);
  },
};

// 微信登录相关 API
export const wechatApi = {
  startLogin: (channel: string, verifier: string, purpose: string = 'login', return_origin?: string) =>
    getApi().post('/api/auth/wechat/login/start', { channel, verifier, purpose, return_origin }, { timeout: 30000 }),
  checkLogin: (state: string, verifier: string) =>
    getApi().post('/api/auth/wechat/login/check', { state, verifier }, { timeout: 30000 }),
  cancelLogin: (state: string, verifier: string) =>
    getApi().post('/api/auth/wechat/login/cancel', { state, verifier }, { timeout: 30000 }),
  completeNativeLogin: (state: string, verifier: string, code: string) =>
    getApi().post('/api/auth/wechat/native/complete', { state, verifier, code }, { timeout: 30000 }),

  // 检查微信登录是否已配置
  getConfig: () => getApi().get('/api/auth/wechat/config', { timeout: 30000 }),
  // 创建扫码登录会话（获取二维码）
  createScanLogin: () => getApi().get('/api/auth/wechat/scan-login/create'),
  // 轮询检查扫码登录状态
  checkScanLogin: (scene: string) =>
    getApi().get('/api/auth/wechat/scan-login/check', { params: { scene } }),
  // 小程序确认扫码登录
  confirmScanLogin: (data: { scene: string; code: string; nickname?: string; avatar_url?: string }) =>
    getApi().post('/api/auth/wechat/scan-login/confirm', data),
  // 小程序内直接登录
  miniappLogin: (data: { code: string; nickname?: string; avatar_url?: string; device_name?: string }) =>
    getApi().post('/api/auth/wechat/miniapp-login', data),
  // 安卓端微信跳转登录
  getMobileLoginUrl: () => getApi().get('/api/auth/wechat/mobile-login/url'),
  consumeMobileLogin: (result: string) =>
    getApi().post('/api/auth/wechat/mobile-login/consume', { result }),
  // 绑定微信
  bind: (code: string) => getApi().post('/api/auth/wechat/bind', { code }),
  // 解绑微信
  unbind: () => getApi().post('/api/auth/wechat/unbind'),
};

// 用户管理 API（管理员）
export const usersApi = {
  getAll: (params?: { search?: string; role?: string; is_active?: string; page?: number; page_size?: number }) =>
    getApi().get('/api/users', { params }),
  getById: (id: number) => getApi().get(`/api/users/${id}`),
  create: (data: any) => getApi().post('/api/users/create', data),
  update: (id: number, data: any) => getApi().put(`/api/users/${id}/update`, data),
  delete: (id: number) => getApi().delete(`/api/users/${id}/delete`),
  resetPassword: (id: number, newPassword: string) =>
    getApi().post(`/api/users/${id}/reset-password`, { new_password: newPassword }),
  setRoles: (id: number, roleIds: number[]) =>
    getApi().put(`/api/users/${id}/roles`, { role_ids: roleIds }),
  toggleActive: (id: number) => getApi().put(`/api/users/${id}/toggle-active`),
};

// 角色管理 API
export const rolesApi = {
  getAll: () => getApi().get('/api/roles'),
  getById: (id: number) => getApi().get(`/api/roles/${id}`),
  create: (data: any) => getApi().post('/api/roles/create', data),
  update: (id: number, data: any) => getApi().put(`/api/roles/${id}/update`, data),
  delete: (id: number) => getApi().delete(`/api/roles/${id}/delete`),
  setPermissions: (id: number, permissionIds: number[]) =>
    getApi().put(`/api/roles/${id}/permissions`, { permission_ids: permissionIds }),
};

// 权限管理 API
export const permissionsApi = {
  getAll: (module?: string) => getApi().get('/api/permissions', { params: module ? { module } : {} }),
  getModules: () => getApi().get('/api/permissions/modules'),
};

// 操作日志 API
export const operationLogsApi = {
  getAll: (params?: { user_id?: string; action?: string; resource?: string; page?: number; page_size?: number }) =>
    getApi().get('/api/operation-logs', { params }),
};

// Token 管理：设置/获取/清除本地存储的 token
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export function setAuthToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  // 重置 api 实例以便拦截器生效
  apiInstance = null;
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  apiInstance = null;
}

export function setAuthUser(user: any) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getAuthUser(): any | null {
  try {
    const s = localStorage.getItem(USER_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

// 将后端返回的相对路径（例如 /media/xxx）转换为可在前端(3000端口/Capacitor)访问的绝对地址
export function resolveApiUrl(url: string): string {
  if (!url) return url;
  // 已经是绝对地址
  if (/^https?:\/\//i.test(url)) return url;
  const base = getApiBaseUrl().replace(/\/$/, '');
  if (url.startsWith('/')) return `${base}${url}`;
  return `${base}/${url}`;
}

export interface MissionAction {
  type: 'meal' | 'exercise' | 'note' | 'study' | 'manual';
  label: string;
  pathwayId?: string;
}

export interface MissionProgress {
  current: number;
  target: number;
  unit: string;
  percent: number;
  label: string;
}

export interface WeeklyMissionTask {
  id: string | number;
  template_key?: string;
  dimension: string;
  title: string;
  description?: string;
  exp?: number;
  status: 'pending' | 'completed';
  completed_at?: string | null;
  is_boost?: boolean;
  body_focus?: string;
  difficulty?: string;
  verification?: 'auto' | 'manual';
  rule?: { metric: string; target: number; unit: string; [key: string]: unknown };
  progress?: MissionProgress;
  action?: MissionAction;
  reason?: string;
  estimated_minutes?: number;
  priority?: number;
  archived?: boolean;
}

export interface MissionOption {
  key: string;
  name: string;
  icon?: string;
  color?: string;
  description?: string;
}

export interface WeeklyMissionConfig {
  selected_dimensions: string[];
  body_focus: string;
  body_difficulty: string;
  daily_minutes?: number;
  weekly_task_limit?: number;
  profile?: { age: number | null; height_cm: number | null; weight_kg: number | null; health_notes: string };
  dimension_options: MissionOption[];
  body_focus_options: MissionOption[];
  body_difficulty_options: MissionOption[];
}

export interface BodyMissionContext {
  requested_difficulty: string;
  effective_difficulty: string;
  was_adjusted: boolean;
  readiness_score: number;
  readiness_level: string;
  restrictions: Array<{ code: string; title: string; detail: string; severity: string }>;
  recommended_intensity: string;
  recommended_duration: string;
  disclaimer: string;
}

export interface WeeklyMissionGroup {
  key: string;
  name: string;
  icon: string;
  color: string;
  score: number;
  is_low: boolean;
  tasks: WeeklyMissionTask[];
  total: number;
  completed: number;
}

export interface WeeklyMissionPayload {
  week_start: string;
  total: number;
  completed: number;
  pct: number;
  groups: WeeklyMissionGroup[];
  config: WeeklyMissionConfig;
  body_context?: BodyMissionContext | null;
  weekly_report?: { summary?: string; observation?: string; suggestion?: string; mode?: 'ai' | 'rules'; ai_status?: 'available' | 'not_configured' | 'quota_exhausted' | 'unavailable' };
  source_snapshot?: { profile?: { assumed_fields?: string[] }; exercise?: { sessions: number; minutes: number }; diet?: { recorded_days: number }; study?: { notes: number; pathway_tasks_completed: number } };
  daily_study?: Array<{ pathway_id: string; task_id: string; title: string; description: string; status: string }>;
}

// 人员相关API
export const peopleApi = {
  getAll: () => getApi().get('/api/people'),
  getById: (id: number) => getApi().get(`/api/people/${id}`),
  create: (data: any) =>
    getApi().post('/api/people', data),
  update: (id: number, data: any) =>
    getApi().put(`/api/people/${id}`, data),
  delete: (id: number) => getApi().delete(`/api/people/${id}`),
  getDashboard: () => getApi().get('/api/people/dashboard'),
  quickContact: (personId: number, note?: string) =>
    getApi().post(`/api/people/${personId}/quick-contact`, { note }),
  autoStrength: (personId: number) =>
    getApi().post(`/api/people/${personId}/auto-strength`),
};

// 日记相关API
export const diariesApi = {
  getAll: (personId?: number) => {
    const params = personId ? { person_id: personId } : {};
    return getApi().get('/api/diaries', { params });
  },
  searchNotes: (query = '') => getApi().get('/api/diaries', {
    params: { diary_type: 'note', home_preview: '1', ...(query.trim() ? { q: query.trim() } : {}) },
  }),
  getById: (id: number) => getApi().get(`/api/diaries/${id}`),
  create: (data: { person_id?: number | null; content: string; date?: string; diary_type?: string; mood?: string; is_public?: boolean }) =>
    getApi().post('/api/diaries', data),
  update: (id: number, content: string, date?: string) =>
    getApi().put(`/api/diaries/${id}`, { content: content, date: date }),
  delete: (id: number) => getApi().delete(`/api/diaries/${id}`),

  uploadImages: (diaryId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    // 不手动设置 Content-Type，让浏览器自动携带 multipart boundary
    return getApi().post(`/api/diaries/${diaryId}/images`, formData);
  },

  deleteImage: (diaryId: number, imageId: number) =>
    getApi().delete(`/api/diaries/${diaryId}/images/${imageId}`),
};

// 分析相关API
export const analysisApi = {
  getByPersonId: (personId: number) => getApi().get(`/api/analysis/${personId}`),
  analyze: (personId: number) => getApi().post(`/api/analysis/${personId}`),
  getAll: () => getApi().get('/api/analysis'),
};

// 关系相关API
export const relationshipsApi = {
  create: (data: { person1_id: number; person2_id: number; relationship_type: string; description?: string; strength?: number }) =>
    getApi().post('/api/relationships', data),
  getAll: (personId?: number) => {
    const params = personId ? { person_id: personId } : {};
    return getApi().get('/api/relationships', { params });
  },
  update: (person1Id: number, person2Id: number, data: { relationship_type?: string; description?: string; strength?: number }) =>
    getApi().put('/api/relationships', { person1_id: person1Id, person2_id: person2Id, ...data }),
  delete: (person1Id: number, person2Id: number) =>
    getApi().delete(`/api/relationships/${person1Id}/${person2Id}`),
  getGraph: () => getApi().get('/api/relationships/graph'),
};

// 游戏化相关API
export const gamificationApi = {
  // 初始化成就系统
  init: () => getApi().get('/api/gamification/init'),
  
  // 用户统计
  getStats: () => getApi().get('/api/gamification/stats'),
  
  // 成就相关
  getAllAchievements: () => getApi().get('/api/gamification/achievements'),
  getUserAchievements: (unlockedOnly: boolean = false) => 
    getApi().get('/api/gamification/achievements/user', { params: { unlocked_only: unlockedOnly } }),
  
  // 经验值日志
  getExpLogs: (limit: number = 20) => 
    getApi().get('/api/gamification/exp-logs', { params: { limit } }),
  
  // 任务相关
  getTasks: (status?: string) => {
    const params = status ? { status } : {};
    return getApi().get('/api/gamification/tasks', { params });
  },
  getTask: (taskId: number) => getApi().get(`/api/gamification/tasks/${taskId}`),
  createTask: (data: {
    title: string;
    description?: string;
    task_type?: string;
    target_person_id?: number;
    is_recurring?: boolean;
    recurrence_pattern?: string;
    due_date?: string;
    reminder_date?: string;
    exp_reward?: number;
  }) => getApi().post('/api/gamification/tasks', data),
  updateTask: (taskId: number, data: any) => getApi().put(`/api/gamification/tasks/${taskId}`, data),
  completeTask: (taskId: number) => getApi().post(`/api/gamification/tasks/${taskId}/complete`),
  deleteTask: (taskId: number) => getApi().delete(`/api/gamification/tasks/${taskId}`),
  
  // 仪表板
  getDashboard: () => getApi().get('/api/gamification/dashboard'),

  // 本周六维任务中心（与 Django Web /missions/ 一致）
  getWeeklyMission: () => getApi().get<WeeklyMissionPayload>('/api/gamification/weekly-mission'),
  generateWeeklyMission: (data: {
    selected_dimensions: string[];
    body_focus: string;
    body_difficulty: string;
    daily_minutes?: number;
    weekly_task_limit?: number;
    profile?: { age: number | null; height_cm: number | null; weight_kg: number | null; health_notes: string };
  }) => getApi().post<WeeklyMissionPayload>('/api/gamification/weekly-mission/generate', data, { timeout: 45000 }),
  completeWeeklyMissionTask: (taskId: string | number) =>
    getApi().post<WeeklyMissionPayload>(`/api/gamification/weekly-mission/${encodeURIComponent(String(taskId))}/complete`),

  // 维度页
  getIntellect: () => getApi().get('/api/gamification/intellect'),
  getPurpose: () => getApi().get('/api/gamification/purpose'),
};

// MBTI测试相关API
export const mbtiApi = {
  // 获取测试题目
  getQuestions: () => getApi().get('/api/mbti/questions'),
  
  // 提交测试
  submitTest: (data: { person_id?: number | null; answers: Record<string, string> }) =>
    getApi().post('/api/mbti/submit', data),
  
  // 获取所有结果
  getAllResults: () => getApi().get('/api/mbti/results'),
  
  // 获取单个结果
  getResult: (resultId: number) => getApi().get(`/api/mbti/results/${resultId}`),
  
  // 获取某人的结果
  getPersonResults: (personId: number) => getApi().get(`/api/mbti/results/person/${personId}`),
  
  // 获取自己的最新结果
  getSelfLatestResult: () => getApi().get('/api/mbti/results/self/latest'),
  
  // 删除结果
  deleteResult: (resultId: number) => getApi().delete(`/api/mbti/results/${resultId}`),
  
  // 获取所有类型信息
  getAllTypes: () => getApi().get('/api/mbti/types'),
  
  // 获取特定类型信息
  getTypeInfo: (mbtiType: string) => getApi().get(`/api/mbti/types/${mbtiType}`),
  
  // 获取兼容性分析
  getCompatibility: (type1: string, type2: string) => 
    getApi().get(`/api/mbti/compatibility/${type1}/${type2}`),
};

// 体魄锻造（健身）相关API
export const fitnessApi = {
  // 获取健身总览
  getSummary: () => getApi().get('/api/fitness/summary'),

  // 获取今日任务
  getTodayTasks: () => getApi().get('/api/fitness/today'),

  // 获取本周任务
  getWeekTasks: () => getApi().get('/api/fitness/week'),

  // 完成任务
  completeTask: (taskId: number) => getApi().post(`/api/fitness/tasks/${taskId}/complete`),

  // 获取本周计划
  getWeeklyPlan: () => getApi().get('/api/fitness/weekly-plan'),

  // 获取精力热力图数据
  getEnergyHeatmap: (weeks?: number) =>
    getApi().get('/api/fitness/energy-heatmap', { params: weeks ? { weeks } : {} }),

  getWellness: () => getApi().get('/api/fitness/wellness'),
  getExerciseDashboard: () => getApi().get('/api/fitness/exercise/dashboard'),
  getExerciseSessions: (type?: string) =>
    getApi().get('/api/fitness/exercise/sessions', { params: type ? { type } : {} }),
  createExerciseSession: (data: any) => getApi().post('/api/fitness/exercise/sessions', data),
  deleteExerciseSession: (sessionId: number) => getApi().delete(`/api/fitness/exercise/sessions/${sessionId}`),
  updateExerciseGoal: (data: {
    weekly_sessions: number;
    weekly_minutes: number;
    weekly_calories: number;
    primary_goal: string;
    max_heart_rate: number;
  }) =>
    getApi().put('/api/fitness/exercise/goal', data),
};

// 人生任务相关API
export const lifeGoalsApi = {
  getDashboard: (status?: string) =>
    getApi().get('/api/life-goals/dashboard', { params: status ? { status } : {} }),
  getAll: (status?: string) => getApi().get('/api/life-goals', { params: status ? { status } : {} }),
  create: (data: {
    title: string;
    description?: string;
    category?: string;
    priority?: string;
    icon?: string;
    target_date?: string;
    time_frame?: string;
    parent_goal_id?: number | null;
    milestones?: string[];
  }) => getApi().post('/api/life-goals', data),
  createFromTemplate: (templateKey: string, parentGoalId?: number) =>
    getApi().post('/api/life-goals/from-template', {
      template_key: templateKey,
      parent_goal_id: parentGoalId,
    }),
  getById: (goalId: number) => getApi().get(`/api/life-goals/${goalId}`),
  update: (goalId: number, data: any) => getApi().put(`/api/life-goals/${goalId}`, data),
  delete: (goalId: number) => getApi().delete(`/api/life-goals/${goalId}`),
  submitReview: (goalId: number, data: {
    progress?: number;
    achievements?: string;
    obstacles?: string;
    lessons?: string;
    adjustments?: string;
  }) => getApi().post(`/api/life-goals/${goalId}/review`, data),
  addMilestone: (goalId: number, title: string) => getApi().post(`/api/life-goals/${goalId}/milestones`, { title }),
  toggleMilestone: (goalId: number, milestoneId: number) => getApi().post(`/api/life-goals/${goalId}/milestones/${milestoneId}/toggle`),
  deleteMilestone: (goalId: number, milestoneId: number) => getApi().delete(`/api/life-goals/${goalId}/milestones/${milestoneId}`),
};

// 神之途径相关API
export const pathwayApi = {
  // 获取所有途径
  getAllPathways: () => getApi().get('/api/pathway/pathways'),
  
  // 获取单个途径
  getPathway: (pathwayId: string) => getApi().get(`/api/pathway/pathways/${pathwayId}`),
  
  // 获取所有进度
  getAllProgress: () => getApi().get('/api/pathway/progress'),
  
  // 获取某条途径的进度
  getProgress: (pathwayId: string) => getApi().get(`/api/pathway/progress/${pathwayId}`),
  
  // 获取答题题目
  getQuiz: (pathwayId: string, sequenceLevel: number) => 
    getApi().get(`/api/pathway/quiz/${pathwayId}/${sequenceLevel}`),
  
  // 提交答题
  submitQuiz: (data: { pathway_id: string; sequence_level: number; answers: Record<string, number>; quiz_token: string }) =>
    getApi().post('/api/pathway/quiz/submit', data),
  
  // 获取答题历史
  getQuizHistory: (pathwayId: string) => getApi().get(`/api/pathway/quiz-history/${pathwayId}`),

  // 个性化升级考核（非答题）：获取当前序列的考核方式与达标进度
  getVerificationInfo: (pathwayId: string) => getApi().get(`/api/pathway/verification/${pathwayId}`),

  // 提交个性化升级考核（practice/streak/reflection/milestone）
  submitVerification: (pathwayId: string, data: { sequence_level: number; reflection?: string; milestone_confirmed?: boolean; evidence_url?: string }) =>
    getApi().post(`/api/pathway/verification/${pathwayId}/submit`, data),
  
  // 获取仪表板
  getDashboard: () => getApi().get('/api/pathway/dashboard'),

  // 获取今日每日任务（不存在则后端生成）
  getTodayDailyTasks: (pathwayId: string) => getApi().get(`/api/pathway/daily-tasks/${pathwayId}`),
  addDailyTask: (pathwayId: string, taskId: number | string) =>
    getApi().post(`/api/pathway/daily-tasks/${pathwayId}/add`, { task_id: taskId }),

  // 完成某个每日任务
  completeDailyTask: (pathwayId: string, taskId: number | string) =>
    getApi().post(`/api/pathway/daily-tasks/${pathwayId}/complete`, { task_id: taskId }),

  // ===== 途径订阅 =====
  // 获取我的所有订阅途径（附带本周任务进度）
  getSubscriptions: () => getApi().get('/api/pathway/subscriptions'),

  // 订阅途径
  subscribe: (pathwayId: string) => getApi().post(`/api/pathway/subscriptions/${pathwayId}`),

  // 取消订阅途径
  unsubscribe: (pathwayId: string) => getApi().delete(`/api/pathway/subscriptions/${pathwayId}/unsubscribe`),

  // ===== 每周任务 =====
  // 获取本周途径任务
  getWeeklyTasks: (pathwayId: string) => getApi().get(`/api/pathway/weekly-tasks/${pathwayId}`),

  // 完成本周某个途径任务
  completeWeeklyTask: (pathwayId: string, taskId: number | string) =>
    getApi().post(`/api/pathway/weekly-tasks/${pathwayId}/complete`, { task_id: taskId }),
};

// 营养管理相关API
export const nutritionApi = {
  // 获取食物列表
  getFoods: (params?: { category?: string; search?: string; limit?: number }) =>
    getApi().get('/api/nutrition/foods', { params }),

  // 创建自定义食物
  createFood: (data: { name: string; aliases?: string; pinyin?: string; category?: string; icon?: string; calories?: number; protein?: number; fat?: number; carbs?: number; fiber?: number }) =>
    getApi().post('/api/nutrition/foods/create', data),

  setFoodFavorite: (foodId: number, favorite: boolean) => favorite
    ? getApi().post(`/api/nutrition/foods/${foodId}/favorite`)
    : getApi().delete(`/api/nutrition/foods/${foodId}/favorite`),

  getQuickOptions: () => getApi().get('/api/nutrition/quick-options'),

  // 获取某天的饮食记录
  getMeals: (date?: string) =>
    getApi().get('/api/nutrition/meals', { params: date ? { date } : {} }),
  searchMealHistory: (query = '') => getApi().get('/api/nutrition/meals/history', {
    params: query.trim() ? { q: query.trim() } : {},
  }),

  // 添加饮食记录
  createMeal: (data: {
    food_name: string;
    meal_type: string;
    amount?: number;
    food_item_id?: number;
    date?: string;
    calories?: number;
    protein?: number;
    fat?: number;
    carbs?: number;
    fiber?: number;
    sugar?: number;
    sodium?: number;
    potassium?: number;
    cholesterol?: number;
    saturated_fat?: number;
    unsaturated_fat?: number;
    trans_fat?: number;
    vitamin_c?: number;
    calcium?: number;
    iron?: number;
    zinc?: number;
    magnesium?: number;
    phosphorus?: number;
    selenium?: number;
    vitamin_a?: number;
    vitamin_b1?: number;
    vitamin_b2?: number;
    vitamin_b6?: number;
    vitamin_b12?: number;
    vitamin_d?: number;
    vitamin_e?: number;
    vitamin_k?: number;
    folate?: number;
    niacin?: number;
    water?: number;
    caffeine?: number;
    alcohol?: number;
    note?: string;
  }) =>
    getApi().post('/api/nutrition/meals/create', data),

  createMealsBatch: (data: {
    meal_type: string;
    date?: string;
    items: Array<{
      food_name: string;
      amount?: number;
      food_item_id?: number;
      calories?: number;
      protein?: number;
      fat?: number;
      carbs?: number;
      fiber?: number;
      sugar?: number;
      sodium?: number;
      potassium?: number;
      cholesterol?: number;
      saturated_fat?: number;
      unsaturated_fat?: number;
      trans_fat?: number;
      vitamin_c?: number;
      calcium?: number;
      iron?: number;
      zinc?: number;
      magnesium?: number;
      phosphorus?: number;
      selenium?: number;
      vitamin_a?: number;
      vitamin_b1?: number;
      vitamin_b2?: number;
      vitamin_b6?: number;
      vitamin_b12?: number;
      vitamin_d?: number;
      vitamin_e?: number;
      vitamin_k?: number;
      folate?: number;
      niacin?: number;
      water?: number;
      caffeine?: number;
      alcohol?: number;
      note?: string;
    }>;
  }) => getApi().post('/api/nutrition/meals/batch-create', data),

  copyMeals: (data: { record_ids: number[]; date?: string; meal_type?: string }) =>
    getApi().post('/api/nutrition/meals/copy', data),

  // 编辑饮食记录（未提供的营养字段由后端保留或按份量缩放）
  updateMeal: (recordId: number, data: {
    food_name?: string;
    meal_type?: string;
    amount?: number;
    food_item_id?: number | null;
    date?: string;
    calories?: number;
    note?: string;
  }) => getApi().patch(`/api/nutrition/meals/${recordId}`, data),

  // 删除饮食记录
  deleteMeal: (recordId: number) =>
    getApi().delete(`/api/nutrition/meals/${recordId}/delete`),

  getHistoryMeta: () => getApi().get('/api/nutrition/history-meta'),

  // 获取每日营养汇总
  getDailySummary: (date?: string) =>
    getApi().get('/api/nutrition/summary', { params: date ? { date } : {} }),

  // 获取每周营养汇总
  getWeeklySummary: () =>
    getApi().get('/api/nutrition/weekly'),

  // 获取近期、周度或月度饮食回顾
  getReview: (period: 'recent' | 'week' | 'month', date?: string) =>
    getApi().get('/api/nutrition/review', { params: { period, ...(date ? { date } : {}) } }),

  // 记录饮水
  addWater: (data?: { cups?: number; date?: string }) =>
    getApi().post('/api/nutrition/water', data || {}),

  // 获取/更新营养目标
  getGoal: () => getApi().get('/api/nutrition/goal'),
  updateGoal: (data: { target_calories?: number; target_protein?: number; target_fat?: number; target_carbs?: number; target_fiber?: number; target_water_cups?: number }) =>
    getApi().put('/api/nutrition/goal', data),

  quickAiSnap: (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return getApi().post('/api/nutrition/ai-snap', formData, { timeout: 90000 });
  },

  quickAiSnapAnalyze: (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return getApi().post('/api/nutrition/ai-snap/analyze', formData, { timeout: 90000 });
  },

  getAiSnapItemInfo: (itemName: string) =>
    getApi().post('/api/nutrition/ai-snap/item-info', { item_name: itemName }, { timeout: 45000 }),

  getAiSnapSuggestions: (analysis: any) =>
    getApi().post('/api/nutrition/ai-snap/suggestions', { analysis }, { timeout: 45000 }),
};

// 泛灵之网相关API
export const panSpiritApi = {
  // 获取公开日记列表
  getFeed: () => getApi().get('/api/pan-spirit/feed'),

  // 点赞/取消点赞
  toggleLike: (diaryId: number) => getApi().post(`/api/pan-spirit/${diaryId}/like`),

  // 获取评论列表
  getComments: (diaryId: number) => getApi().get(`/api/pan-spirit/${diaryId}/comments`),

  // 发表评论
  addComment: (diaryId: number, content: string) =>
    getApi().post(`/api/pan-spirit/${diaryId}/comments`, { content }),

  // 删除评论
  deleteComment: (diaryId: number, commentId: number) =>
    getApi().delete(`/api/pan-spirit/${diaryId}/comments/${commentId}`),

  // 获取我的锚点
  getMyAnchor: () => getApi().get('/api/pan-spirit/my-anchor'),
};

// 健康管理相关API
export const healthApi = {
  getReports: () => getApi().get('/api/health/reports'),

  uploadReport: (formData: FormData) =>
    getApi().post('/api/health/reports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getReportDetail: (reportId: number) =>
    getApi().get(`/api/health/reports/${reportId}`),

  deleteReport: (reportId: number) =>
    getApi().delete(`/api/health/reports/${reportId}/delete`),

  reparseReport: (reportId: number) =>
    getApi().post(`/api/health/reports/${reportId}/reparse`),

  getMetrics: (params?: { name?: string; code?: string; category?: string }) =>
    getApi().get('/api/health/metrics', { params }),

  /** 按指标 code 聚合的目录（每项独立时间序列） */
  getMetricsCatalog: () => getApi().get('/api/health/metrics/catalog'),

  /** 单项指标全部存储记录 + 趋势 */
  getMetricByCode: (code: string) =>
    getApi().get(`/api/health/metrics/code/${encodeURIComponent(code)}`),

  getMetricsSummary: () =>
    getApi().get('/api/health/metrics/summary'),

  getMetricTrend: (metricName: string) =>
    getApi().get(`/api/health/metrics/trend/${encodeURIComponent(metricName)}`),

  getMetricTrendByCode: (code: string) =>
    getApi().get(`/api/health/metrics/trend/code/${encodeURIComponent(code)}`),
};

// ==================== 模块化健康管理 API ====================
export const healthModulesApi = {
  // 仪表盘
  getDashboard: () => getApi().get('/api/health/dashboard'),
  calculateScore: () => getApi().post('/api/health/score/calculate'),

  // 血压
  getBpRecords: (days?: number) => getApi().get('/api/health/bp', { params: { days } }),
  addBpRecord: (data: { systolic: number; diastolic: number; heart_rate?: number; measured_at?: string; period?: string; note?: string }) =>
    getApi().post('/api/health/bp', data),
  deleteBpRecord: (id: number) => getApi().delete(`/api/health/bp/${id}/delete`),
  getBpStats: () => getApi().get('/api/health/bp/stats'),
  getBpTrend: (days?: number) => getApi().get('/api/health/bp/trend', { params: { days } }),

  // 体重
  getWeightRecords: (days?: number) => getApi().get('/api/health/weight', { params: { days } }),
  addWeightRecord: (data: { weight: number; height?: number; body_fat_pct?: number; muscle_mass?: number; measured_at?: string; note?: string }) =>
    getApi().post('/api/health/weight', data),
  deleteWeightRecord: (id: number) => getApi().delete(`/api/health/weight/${id}/delete`),
  getWeightTrend: (days?: number) => getApi().get('/api/health/weight/trend', { params: { days } }),

  // 血糖
  getSugarRecords: (days?: number) => getApi().get('/api/health/sugar', { params: { days } }),
  addSugarRecord: (data: { value: number; measurement_type?: string; measured_at?: string; note?: string }) =>
    getApi().post('/api/health/sugar', data),
  deleteSugarRecord: (id: number) => getApi().delete(`/api/health/sugar/${id}/delete`),
  getSugarStats: () => getApi().get('/api/health/sugar/stats'),
  getSugarTrend: (days?: number) => getApi().get('/api/health/sugar/trend', { params: { days } }),

  // 心率
  getHrRecords: (days?: number) => getApi().get('/api/health/hr', { params: { days } }),
  addHrRecord: (data: { heart_rate: number; state?: string; measured_at?: string; note?: string }) =>
    getApi().post('/api/health/hr', data),
  deleteHrRecord: (id: number) => getApi().delete(`/api/health/hr/${id}/delete`),

  // 自定义健康指标
  getPersonalMetrics: (days?: number) => getApi().get('/api/health/personal-metrics', { params: { days } }),
  addPersonalMetric: (data: {
    name: string;
    unit?: string;
    category?: string;
    reference_min?: number;
    reference_max?: number;
    target_text?: string;
    initial_value?: number;
    measured_at?: string;
    note?: string;
  }) => getApi().post('/api/health/personal-metrics', data),
  deletePersonalMetric: (id: number) => getApi().delete(`/api/health/personal-metrics/${id}/delete`),
  addPersonalMetricRecord: (id: number, data: { value: number; measured_at?: string; note?: string }) =>
    getApi().post(`/api/health/personal-metrics/${id}/records`, data),
  deletePersonalMetricRecord: (id: number) => getApi().delete(`/api/health/personal-metric-records/${id}/delete`),

  // 健康目标
  getGoals: () => getApi().get('/api/health/goals'),
  addGoal: (data: { goal_type: string; target_value: number; target_value_2?: number; unit?: string; deadline?: string }) =>
    getApi().post('/api/health/goals', data),
  deleteGoal: (id: number) => getApi().delete(`/api/health/goals/${id}/delete`),
};

// 知识考试相关API
export const examApi = {
  getCategories: () => getApi().get('/api/exam/categories'),
  getPracticePathways: (category?: string) =>
    getApi().get('/api/exam/practice-pathways', { params: category ? { category } : {} }),
  getQuiz: (category?: string, pathwayId?: string) =>
    getApi().get('/api/exam/quiz', { params: { category, pathway_id: pathwayId || undefined } }),
  getNextQuestion: (data: { category: string; pathway_id?: string; exam_token: string; seen_ids: number[] }) =>
    getApi().post('/api/exam/quiz/next', data),
  checkAnswer: (data: { category: string; pathway_id?: string; exam_token: string; question_id: number; chosen: number }) =>
    getApi().post('/api/exam/quiz/check', data),
  submitQuiz: (data: { category: string; pathway_id?: string; answers: Record<string, number>; exam_token: string }) =>
    getApi().post('/api/exam/submit', data),
  getHistory: (days?: number) => getApi().get('/api/exam/history', { params: { days } }),
  getStats: () => getApi().get('/api/exam/stats'),
  getDashboard: () => getApi().get('/api/exam/dashboard'),
};

// 消息通知相关 API
export interface NotificationPreference {
  enabled: boolean;
  daily_task_enabled: boolean;
  daily_task_time: string;            // 'HH:MM'
  daily_task_interval_minutes: number;
  meal_reminder_enabled: boolean;
  breakfast_time: string;
  lunch_time: string;
  dinner_time: string;
  quiet_hours_enabled: boolean;
  quiet_start: string;
  quiet_end: string;
  updated_at?: string;
}

export const notificationsApi = {
  // 注册设备 FCM token
  registerDevice: (data: { token: string; platform?: string; device_name?: string }) =>
    getApi().post('/api/notifications/devices/register', data),
  // 注销设备 token
  unregisterDevice: (token: string) =>
    getApi().post('/api/notifications/devices/unregister', { token }),
  // 读取通知偏好
  getPreference: () => getApi().get<NotificationPreference>('/api/notifications/preference'),
  // 更新通知偏好
  updatePreference: (data: Partial<NotificationPreference>) =>
    getApi().put<NotificationPreference>('/api/notifications/preference', data),
  // 发送测试推送
  testPush: () => getApi().post('/api/notifications/test'),
};

export default getApi;

/** Complete, cursor-paginated history across note, meal and exercise sources. */
export const recordsApi = {
  summary: (params: { period: string; anchor: string }) => getApi().get('/api/records/summary', { params }),
  list: (params: Record<string, string | number>) => getApi().get('/api/records', { params }),
  get: (kind: string, id: number) => getApi().get(`/api/records/${kind}/${id}`),
  update: (kind: string, id: number, data: any) => getApi().patch(`/api/records/${kind}/${id}`, data),
  remove: (kind: string, id: number) => getApi().delete(`/api/records/${kind}/${id}`),
  updateExercise: (id: number, data: any) => getApi().put(`/api/fitness/exercise/sessions/${id}`, data),
};
